import React from 'react'

import { TableAlign, TableHead, TableHeader } from '../../components/Table'
import { FinancialsWidget, getIsCompact } from '../../components/financials-table'
import { ForecastSettings } from '../forecast/types'
import { WidgetHeader } from './WidgetHeader'
import { EarningsChart } from './charts/EarningsChart'
import { BAR_WIDTH, EARNINGS_WIDGET_LABELS, EarningsWidgetCategory } from './constants'
import { EarningsSettings } from './types'
import { EarningReportReaction, EarningsCommonData, EarningsDataEnum, EarningsEstimateType } from './types'
import { useTableColumns } from './useTableColumns'
import { mapData } from './utils'

const widgetToEstimateType: { [K in EarningsWidgetCategory]: EarningsEstimateType } = {
  [EarningsWidgetCategory.Eps]: EarningsEstimateType.eps,
  [EarningsWidgetCategory.EpsGaap]: EarningsEstimateType.epsGaap,
  [EarningsWidgetCategory.Revenue]: EarningsEstimateType.sales,
}

interface Props {
  data: EarningsCommonData
  earningsWidgetCategory: EarningsWidgetCategory
  priceReactionData: EarningReportReaction[]
  settings: EarningsSettings | ForecastSettings
}

export function EarningsWidget({ data, earningsWidgetCategory, priceReactionData, settings }: Props) {
  const [annualData, setAnnualData] = React.useState(() =>
    data ? mapData(data, settings.timeframe, earningsWidgetCategory) : []
  )

  const yearKeys = React.useMemo(
    () =>
      annualData
        .flatMap(({ values }) => values.map(({ year }) => year))
        .filter((year, index, arr) => arr.indexOf(year) === index),
    [annualData]
  )
  const label = EARNINGS_WIDGET_LABELS[earningsWidgetCategory] || ''
  const estimateType = widgetToEstimateType[earningsWidgetCategory]
  const [isCompact, setIsCompact] = React.useState(getIsCompact(yearKeys.length, 22))
  const { columns, subColumns } = useTableColumns(yearKeys, isCompact)

  React.useEffect(() => {
    // Remove min-height from react root element which is set on BE because of CLS
    document.getElementById('root')?.style.removeProperty('min-height')

    const onResize = () => {
      setIsCompact(getIsCompact(yearKeys.length, BAR_WIDTH))
    }

    onResize()

    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [yearKeys])

  React.useEffect(() => {
    setAnnualData(mapData(data, settings.timeframe, earningsWidgetCategory))
  }, [data, settings.timeframe, earningsWidgetCategory])

  const filteredTableData = React.useMemo(
    () =>
      annualData.map((revenue) => ({
        ...revenue,
        subData: revenue.subData.filter(({ id }) => id === EarningsDataEnum.analysts || settings[id] === true),
      })),
    [annualData, settings]
  )

  const onVisibilityChange = React.useCallback((id: string, isSelected: boolean) => {
    setAnnualData((prevAnnualData) =>
      prevAnnualData.map((item) =>
        item.id === id
          ? {
              ...item,
              isSelected,
            }
          : item
      )
    )
  }, [])

  return (
    <div
      style={{
        minWidth: isCompact ? yearKeys.length * 70 + 200 : undefined,
      }}
    >
      <FinancialsWidget
        data={filteredTableData}
        header={<WidgetHeader>{label}</WidgetHeader>}
        tableHead={
          <TableHead>
            <TableHeader align={TableAlign.Left}>Currency: USD</TableHeader>

            {columns.map((column) => (
              <TableHeader key={column.name} align={column.align} className={column.className}>
                {column.name}
              </TableHeader>
            ))}
          </TableHead>
        }
        columns={columns}
        subColumns={subColumns}
        onVisibilityChange={onVisibilityChange}
      >
        <EarningsChart
          annualData={annualData}
          estimateType={estimateType}
          isCompact={isCompact}
          reportReactions={priceReactionData}
        />
      </FinancialsWidget>
    </div>
  )
}
